import {
  FunctionComponent,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { ButtonGroup } from '@atlaskit/button';
import Button from '@atlaskit/button/new';
import { Box, Text, xcss } from '@atlaskit/primitives';
import { media } from '@atlaskit/primitives/compiled';

import { useAnalytics } from '../../Analytics/AnalyticsProvider';
import { ReactConsentContext } from '../../Flow/ConsentContextProvider';
import { type McpContext, ReactMcpContext } from './McpContextProvider';
import { mcpPermissionsMessages } from './McpPermissions/messages';
import LegalDisclaimer from './LegalDisclaimer/LegalDisclaimer';
import {
  getOrderedPolicySources,
  hasFailedPolicyCheck,
  MCP_POLICY_STATUS,
} from './domain/policyState';

const mcpBottomBarStyles = xcss({
  flexShrink: 0,
  borderTopWidth: 'border.width',
  borderTopStyle: 'solid',
  borderTopColor: 'color.border',
  width: '100%',
  boxSizing: 'border-box',
  position: 'fixed',
  bottom: 'space.0',
  left: 'space.0',
  padding: 'space.300',
  backgroundColor: 'elevation.surface',
});

const mcpBottomBarInnerStyle = xcss({
  maxWidth: '1280px',
  margin: '0 auto',
  width: '100%',
});

const mcpFooterLayoutStyles = xcss({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  gap: 'space.150',
  width: '100%',
  [media.above.sm]: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 'space.400',
  },
});

const mcpInfoColumnStyles = xcss({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  gap: 'space.050',
  minWidth: '0',
  textAlign: 'right',
});

const messages = defineMessages({
  accept: {
    id: 'footer.accept',
    defaultMessage: 'Accept',
    description:
      'Button to consent for this app to have the listed permissions',
  },
  cancel: {
    id: 'footer.cancel',
    defaultMessage: 'Cancel',
    description:
      'Button to cancel the consent for this app to have the listed permissions',
  },
});

const actionsOnlyStyles = xcss({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: 'space.200',
  justifyContent: 'flex-end',
  flexShrink: 0,
});

type McpAcceptState = Pick<
  McpContext,
  | 'mcpPolicyStatus'
  | 'mcpByIntent'
  | 'mcpSelectedToolsetNames'
  | 'policies'
  | 'scopeClassification'
  | 'hasOrgMismatch'
>;

/**
 * Every target is optional: a site, a Trello workspace and a Bitbucket
 * workspace can each grant on their own, in any combination.
 *
 * So the gate asks nothing about *what* is selected — only whether some current
 * selection produced permissions, the user kept at least one of them, and no
 * check failed or is still in flight. Per-source stale results are already
 * masked (see getPolicyStateForRequest), so a non-empty `mcpByIntent` always
 * reflects the selection on screen rather than a previous one.
 */
export function mcpAcceptDisabled(mcpContext: McpAcceptState): boolean {
  // Still checking — the toolsets on screen may be from the previous selection.
  if (mcpContext.mcpPolicyStatus === MCP_POLICY_STATUS.loading) {
    return true;
  }
  // A failed check blocks Accept even when another source succeeded: the
  // failing selection's permissions are unknown, so there is nothing safe to
  // consent to. Each selector reports its own failure inline so the user can
  // change that selection and retry.
  if (
    hasFailedPolicyCheck(
      mcpContext.policies,
      getOrderedPolicySources(mcpContext.scopeClassification),
    )
  ) {
    return true;
  }
  // A site and a Bitbucket workspace from different organizations cannot be
  // granted in one consent. Both checks can still succeed, so this is its own
  // gate; both selectors say so inline.
  if (mcpContext.hasOrgMismatch) {
    return true;
  }
  // Accept turns on when there are permissions to grant and at least one is
  // selected.
  if (mcpContext.mcpByIntent.length === 0) {
    return true;
  }
  if (mcpContext.mcpSelectedToolsetNames.size === 0) {
    return true;
  }
  return false;
}

type ButtonDecision = 'accept' | 'cancel';

const MCPFooter: FunctionComponent = () => {
  const analytics = useAnalytics();
  const consentContext = useContext(ReactConsentContext);
  const mcpContext = useContext(ReactMcpContext);
  const [clickedButton, setClickedButton] = useState<ButtonDecision | null>(
    null,
  );

  const fireAnalyticEvent = useCallback(
    (buttonName: string, buttonValue: ButtonDecision) => () => {
      analytics.sendButtonClickEvent(buttonName);
      setTimeout(() => setClickedButton(buttonValue), 0);
    },
    [analytics],
  );

  const recordConsentedTrelloWorkspaces =
    mcpContext?.recordConsentedTrelloWorkspaces;

  // Runs synchronously: the consent form is a document POST, so the page
  // unloads immediately and a deferred write would be lost.
  const handleAcceptClick = useCallback(() => {
    recordConsentedTrelloWorkspaces?.();
    fireAnalyticEvent('acceptConsent', 'accept')();
  }, [recordConsentedTrelloWorkspaces, fireAnalyticEvent]);

  const acceptDisabled = useMemo(() => {
    if (!consentContext?.token) {
      return false;
    }
    if (consentContext.token.serviceType === 'MCP') {
      if (!mcpContext) {
        return true;
      }
      return mcpAcceptDisabled(mcpContext);
    }
    // For non-MCP service types, no additional disable conditions
    return false;
  }, [consentContext, mcpContext]);

  const buttons = (
    <ButtonGroup>
      <Button
        type="submit"
        appearance="subtle"
        name="decision"
        value="cancel"
        onClick={fireAnalyticEvent('cancelConsent', 'cancel')}
        isDisabled={clickedButton !== null}
      >
        <FormattedMessage {...messages.cancel} />
      </Button>
      <Button
        type="submit"
        appearance="primary"
        name="decision"
        value="accept"
        isDisabled={acceptDisabled || clickedButton !== null}
        onClick={handleAcceptClick}
      >
        <FormattedMessage {...messages.accept} />
      </Button>
    </ButtonGroup>
  );

  // The caption describes the permissions list, so it shows only while that
  // list is on screen — mirroring McpPermissions. Nothing granted yet, or an
  // empty state standing in for the list (a failure panel, or "permissions are
  // blocked"), and the caption would be counting something invisible. An inline
  // message under a selector doesn't replace the list, so it keeps counting —
  // except for a cross-org conflict, which withdraws the list entirely.
  const showToolsetsCount =
    consentContext?.token.serviceType === 'MCP' &&
    (mcpContext?.mcpByIntent.length ?? 0) > 0 &&
    !mcpContext?.mcpPolicyErrorKind &&
    !mcpContext?.hasOrgMismatch;

  return (
    <Box xcss={mcpBottomBarStyles}>
      <Box xcss={mcpBottomBarInnerStyle}>
        <Box xcss={mcpFooterLayoutStyles}>
          <Box xcss={mcpInfoColumnStyles}>
            {showToolsetsCount && mcpContext && (
              <Text as="span" color="color.text" weight="medium">
                <FormattedMessage
                  {...mcpPermissionsMessages.toolsetsSelected}
                  values={{
                    selected: mcpContext.mcpSelectedToolsetNames.size,
                    total: mcpContext.mcpByIntent.reduce(
                      (n, g) => n + g.toolsets.length,
                      0,
                    ),
                  }}
                />
              </Text>
            )}
            <LegalDisclaimer />
          </Box>
          <Box xcss={actionsOnlyStyles}>{buttons}</Box>
        </Box>
      </Box>
    </Box>
  );
};

export default MCPFooter;
